import React, { useState } from 'react';
import { Tabs } from '@shared/ui/tabs';
import { useTranslation } from 'react-i18next';
import { GenericTable } from '@shared/components/GenericTable';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { RefreshCw, Wrench, MessageSquare, CheckCircle, PowerOff } from 'lucide-react';
import { PageHeader } from '@shared/components/PageHeader';
import { Button } from "@shared/ui/button";
import { Label } from "@shared/ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@shared/ui/select";
import { ActionCard } from '@shared/components/ActionCard';
import { RequestDetailsModal } from '../details/components/RequestDetailsModal';
import { useConsumerRequests, useRequestStatuses } from '../hook';
import { requestsApi } from '../api';
import { getRemoteUtilityId } from '@shared/utils/getUtilityId';
import { getLoginDataFromStorage } from '@shared/utils/loginUtils';
import { PAGE_SIZE } from '@shared/utils/constants';
import { useConsumerDetail } from '../../shared/hook';

const getMockColumns = (t: any): any[] => [
 { header: t("Request ID"), key:'requestNo'as const },
 { 
 header: t("Request Type"), 
 key:'requestType'as const,
 type:'badge',
 badgeConfig: {
  getVariant: () =>'secondary'
 }
 },
 { 
 header: t("Status"), 
 key:'statusDisplay'as const,
 type:'badge',
 badgeConfig: {
  getVariant: (value: string) => {
  if (value ==='Completed') return'success';
  if (value ==='In Progress') return'info';
  return'warning';
  }
 }
 },
 { 
  header: t("Created On"), 
  key:'createdDate'as const,
  render: (val: string, row: any) => (
   <div className="flex flex-col">
    <span className="font-medium text-slate-800">{val}</span>
    <span className="text-[11px] text-slate-500">{t("By")} {row.createdUserRemoteName}</span>
   </div>
  )
 },
 { 
  header: t("Closed Date"), 
  key:'closeDate'as const,
  render: (val: string) => (val === '-' || !val) ? <span className="text-slate-400 italic text-sm">{t("Ongoing")}</span> : <span className="font-medium text-slate-800">{val}</span>
 },
];

const RequestFilterPanel = ({ currentFilters, onApplyFilters, onResetFilters }: any) => {
 const { t } = useTranslation();
 const { data: statusesData, isLoading } = useRequestStatuses();
 const [tempFilters, setTempFilters] = useState({
  status: currentFilters.status || '',
 });

 React.useEffect(() => {
  setTempFilters({
   status: currentFilters.status ||'',
  });
 }, [currentFilters.status]);

 const handleStatusChange = (value: string) => {
 setTempFilters((prev) => ({ ...prev, status: value ==='ALL_STATUS'?'': value }));
 };

 const handleApply = () => {
 onApplyFilters(tempFilters);
 };

 const handleReset = () => {
 setTempFilters({ status:''});
 onResetFilters();
 };

 return (
 <form onSubmit={e => { e.preventDefault(); handleApply(); }}>
  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
   <div className="space-y-2">
    <Label>{t("Status")}</Label>
    <Select 
    value={tempFilters.status ||'ALL_STATUS'} 
    onValueChange={handleStatusChange}
    >
    <SelectTrigger>
     <SelectValue placeholder={t("Select status")}/>
    </SelectTrigger>
    <SelectContent>
     <SelectGroup>
     <SelectItem value="ALL_STATUS">{t("All Statuses")}</SelectItem>
     {isLoading ? (
       <SelectItem value="LOADING" disabled>{t("Loading...")}</SelectItem>
     ) : (
       statusesData?.result?.map((status: any) => (
         <SelectItem key={status.key} value={status.key.toString()}>
           {t(status.value)}
         </SelectItem>
       ))
     )}
    </SelectGroup>
   </SelectContent>
   </Select>
  </div>
  <div className="col-span-1 md:col-span-3 flex justify-end gap-2">
   <Button type="button"variant="outline"onClick={handleReset}>
   {t("Reset Filters")}
   </Button>
   <Button type="submit">
   {t("Apply Filters")}
   </Button>
  </div>
  </div>
 </form>
 );
};

const RequestTabContent = ({ type, searchParams, setSearchParams, onRowClick }: any) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const limit = PAGE_SIZE || 10;
  
  const currentFilters = { status: searchParams.get('status') || '' };
  const searchTerm = searchParams.get('search_data') || '';

  const { consumerId } = getLoginDataFromStorage() || {};
  const remoteUtilityId = getRemoteUtilityId();

  const requestType = type === 'all' ? undefined : type;

  const { data: requestData, isLoading } = useConsumerRequests({
    page,
    limit,
    consumer_id: consumerId,
    remote_utility_id: remoteUtilityId,
    request_type: requestType,
    search_data: searchTerm || undefined,
    status: currentFilters.status || undefined,
  });


  const handleSearch = (term: string) => {
    const params = new URLSearchParams(searchParams);
    if (term) params.set('search_data', term);
    else params.delete('search_data');
    setSearchParams(params);
  };

  const handleApplyFilters = (newFilters: any) => {
    const params = new URLSearchParams(searchParams);
    if (newFilters.status) params.set('status', newFilters.status);
    else params.delete('status');
    setSearchParams(params);
  };

  const handleResetFilters = () => {
    const params = new URLSearchParams(searchParams);
    params.delete('status');
    params.delete('search_data');
    setSearchParams(params);
  };

  const totalItems = requestData?.count || 0;

  const mockColumns = React.useMemo(() => getMockColumns(t), [t]);

  const tableProps = {
    columns: mockColumns,
    data: requestData?.results,
    loading: isLoading,
    pagination: {
      currentPage: page,
      totalPages: Math.ceil(totalItems / limit) || 1,
      totalItems: totalItems,
      startItem: totalItems === 0 ? 0 : (page - 1) * limit + 1,
      endItem: Math.min(page * limit, totalItems)
    },
    onPageChange: setPage,
    onSearch: handleSearch,
    searchPlaceholder: t("Search requests..."),
    actions: [
      {
        label: t("View Details"),
        icon: 'Eye' as const,
        onClick: onRowClick
      }
    ],
    actionsType: "icons" as const,
    rowKey:'id',
    filterPanel: (
      <RequestFilterPanel
        currentFilters={currentFilters}
        onApplyFilters={handleApplyFilters}
        onResetFilters={handleResetFilters}
      />
    )
  };

  return <GenericTable {...tableProps} />;
};

export const RequestsDashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [tabCounts, setTabCounts] = useState<Record<string, number>>({});
  const { t } = useTranslation();

  const { consumerId ,remoteConsumerNumber} = getLoginDataFromStorage() || {};
  const remoteUtilityId = getRemoteUtilityId();

  const {data:consumerInfo}=useConsumerDetail({
    consumer_no: remoteConsumerNumber,
    remote_utility_id: remoteUtilityId,
  })

  const { data: requestData, isLoading } = useConsumerRequests({
    page: 1,
    limit:50,
    consumer_id: consumerId,
    remote_utility_id: remoteUtilityId,
  });

  const handleRowClick = (row: any) => {
    setSelectedRequest(row);
    setIsDetailsModalOpen(true);
  };

  const getTabContent = (type: string) => (
    <RequestTabContent 
      type={type} 
      searchParams={searchParams} 
      setSearchParams={setSearchParams} 
      onRowClick={handleRowClick} 
    />
  );

  const tabConfigs = {
    all: { label: t("All"), component: getTabContent('all'), count: requestData?.count },
    complaint: { label: t("Complaints"), component: getTabContent('Complaint'), count: requestData?.counts?.complaint ||0 },
    service: { label: t("Service Requests"), component: getTabContent('Service'), count: requestData?.counts?.service ||0 },
    other: { label: t("Other Requests"), component: getTabContent('Other'), count: requestData?.counts?.other ||0 }
  };

  return (
    <div className="p-6 space-y-6">
      <PageHeader title={t("My Requests")} subtitle={t("All your complaints, service requests, and other requests in one place")} />

      <div className={`grid grid-cols-2 md:grid-cols-3 ${['disconnected permanent', 'inactive', 'temporary disconnected'].includes(consumerInfo?.result?.statusDisplay?.toLowerCase()) ? 'lg:grid-cols-5' : 'lg:grid-cols-4'} gap-4`}>
        <ActionCard title={t("Service Request")} icon={<Wrench size={24} />} colorClass="bg-blue-600" onClick={() => navigate('/dashboard/requests/create/service')} />
        <ActionCard title={t("Complaint")} icon={<MessageSquare size={24} />} colorClass="bg-rose-600" onClick={() => navigate('/dashboard/requests/create/complaint')} />
        <ActionCard title={t("Transfer")} icon={<RefreshCw size={24} />} colorClass="bg-violet-600" onClick={() => navigate('/dashboard/requests/create/transfer')} />
        {['disconnected permanent', 'inactive', 'temporary disconnected'].includes(consumerInfo?.result?.statusDisplay?.toLowerCase()) && (
          <ActionCard title={t("Reconnection")} icon={<CheckCircle size={24} />} colorClass="bg-green-600" onClick={() => navigate('/dashboard/requests/create/reconnect')} />
        )}
        <ActionCard title={t("Disconnection")} icon={<PowerOff size={24} />} colorClass="bg-red-600" onClick={() => navigate('/dashboard/requests/create/disconnect')} />
      </div>
      
      <Tabs 
        tabComponents={tabConfigs} 
        defaultValue="all"
     tabsListFullWidth={true}
        tabsListClassName="grid grid-cols-4 w-full"
      />
      <RequestDetailsModal 
        isOpen={isDetailsModalOpen} 
        onClose={() => setIsDetailsModalOpen(false)} 
        requestData={selectedRequest} 
      />
    </div>
  );
};
